import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {OptionsChainService} from '../option-chains.service';
import {isVoid} from '../utils';
import {
    PHA_MAX_PCT_OTM, PHA_MIN_PCT_OTM, PHA_SIDES, PhaExpiration, PhaPremiumChoice, PhaPremiumRow, PhaPremiumTotals, PhaSide, premiumChoiceKeyOf
} from './pha-sheet.model';
import {TradingInstrument} from '../trading-instruments/trading-instrument.class';
import {TradingInstrumentsService} from '../trading-instruments/trading-instruments-service.interface';
import {PhaRowMark} from './session/analysis-changes.builder';
import {optionDisplayRows, PhaOptionDisplayRow} from './pha-option-strategy';

// THE PREMIUM TABLE (CR-20260929-001 M3). A custom row grid (ruling R7), dumb by design: it renders
// the sheet's Premium rows and emits what the trader selected. The name is picked from the
// Underlyings, the expiration from the listed chain, the strike through the shared strike dropdown
// (owner, 2026-09-29 vrequest), the contracts spun/typed 0..available (system-description.md
// section 4/5). The option is always a call in v1.

@Component({
    selector: 'ets-pha-premium-table',
    templateUrl: './pha-premium-table.component.html',
    styleUrls: ['./pha-premium-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhaPremiumTableComponent {

    constructor(private readonly _instruments: TradingInstrumentsService, private readonly _chains: OptionsChainService) {
    }

    /** ticker -> its TradingInstrument: the shared strike dropdown's target (owner, 2026-09-29 vrequest). */
    private readonly _instrumentCache: {[ticker: string]: TradingInstrument} = {};

    @Input() rows: PhaPremiumRow[] = [];
    @Input() showGapRows = true;
    @Output() gapPicked = new EventEmitter<{row: PhaPremiumRow, gap: number}>();
    get displayRows(): PhaOptionDisplayRow<PhaPremiumRow>[] { return optionDisplayRows(this.rows, this.showGapRows); }
    @Input() identityRevision = 0;

    readonly trackDisplay = (_: number, entry: PhaOptionDisplayRow<PhaPremiumRow>): string => this.identityRevision + ':' + entry.row.id;
    onGapChanged(row: PhaPremiumRow, ev: {value: number, event?: any}): void {
        if (!ev || !ev.event || !isFinite(ev.value) || ev.value < 0 || this.gapDisabled(row) || ev.value === row.strategyGap) { return; }
        this.gapPicked.emit({row, gap: ev.value});
    }
    gapDisabled(row: PhaPremiumRow): boolean {
        return !this.unlocked || this.rows.some(r => r.strategyId === row.strategyId && (r.crossedOut || this.isExpired(r) || this.isLocked(r)));
    }

    @Input() totals: PhaPremiumTotals = null;

    /**
     * What a Premium row may be pointed at: the held names and the index options (owner, 2026-09-29),
     * shown as two groups of the select box (owner: "index elements in a bucket of their own").
     */
    @Input()
    set choices(value: PhaPremiumChoice[]) {
        const all = value || [];
        const held = all.filter(c => c.underlyingRowId !== null);
        const index = all.filter(c => c.underlyingRowId === null);
        this.groupedChoices = [
            {key: 'Underlyings', items: held},
            {key: 'Indexes', items: index}
        ].filter(g => g.items.length);
    }

    /** DevExtreme grouped items: [{key, items}]. */
    groupedChoices: Array<{key: string, items: PhaPremiumChoice[]}> = [];

    choicesFor(row: PhaPremiumRow): Array<{key: string, items: PhaPremiumChoice[]}> {
        return row.strategyType === 'Two-for-One' ? this.groupedChoices.filter(g => g.key === 'Underlyings') : this.groupedChoices;
    }

    @Input() unlocked = false;

    /** The session's marks per row id (CR-20260930-001 M2): changed cells, the lock, the block. A new object on every change. */
    @Input() marks: {[rowId: number]: PhaRowMark} = {};

    /** Copy the order block the row belongs to (the section's option changes are packed together). */
    @Output() copyClicked = new EventEmitter<PhaPremiumRow>();
    /** Revoke the row's copied block (back to not-exported). */
    @Output() revokeClicked = new EventEmitter<PhaPremiumRow>();

    markOf(row: PhaPremiumRow): PhaRowMark | null {
        return (this.marks && this.marks[row.id]) || null;
    }

    isExpired(row: PhaPremiumRow): boolean { return !!row.optionTicker && this._chains.isOptionExpired(row.optionTicker); }

    isLocked(row: PhaPremiumRow): boolean {
        return this.rows.some(r => (r.id === row.id || row.strategyId && r.strategyId === row.strategyId)
            && !!this.markOf(r) && this.markOf(r).locked);
    }

    isOverdue(row: PhaPremiumRow): boolean {
        const m = this.markOf(row);
        return !!m && m.overdue;
    }

    canCopy(row: PhaPremiumRow): boolean {
        const m = this.markOf(row);
        return !this.isExpired(row) && !!m && m.changed && m.status === 'Pending';
    }

    /** The before value of a changed cell, as text; null when the cell is unchanged. */
    beforeOf(row: PhaPremiumRow, cell: 'contracts'): string | null {
        const m = this.markOf(row);
        const v = m && m.before ? m.before[cell] : undefined;
        return v === undefined ? null : v;
    }

    /** Both book holdings and proposed trades are present, even on a single quantity-edited row. */
    get mixedMode(): boolean {
        return !!this.totals && !!this.totals.mixed;
    }

    @Output() underlyingPicked = new EventEmitter<{row: PhaPremiumRow, key: string}>();
    @Output() expirationPicked = new EventEmitter<{row: PhaPremiumRow, expiration: PhaExpiration | null}>();
    @Output() strikePicked = new EventEmitter<{row: PhaPremiumRow, strike: number | null}>();
    @Output() contractsPicked = new EventEmitter<{row: PhaPremiumRow, contracts: number}>();
    /** The row's own % OTM box (R15): the sheet re-selects the strike to it. */
    @Output() pctOtmPicked = new EventEmitter<{row: PhaPremiumRow, pct: number}>();
    /** The row's OTM lock icon (R18): the sheet flips the flag; the OTM mover skips a locked row. */
    @Output() otmLockToggled = new EventEmitter<PhaPremiumRow>();
    @Output() removeClicked = new EventEmitter<PhaPremiumRow>();
    @Output() restoreClicked = new EventEmitter<PhaPremiumRow>();
    /** The row's SIDE picked from its dropdown (item 4): the sheet flips the sign of its dollars. */
    @Output() sideChanged = new EventEmitter<{row: PhaPremiumRow, side: PhaSide}>();

    readonly centered = {style: 'text-align: center;'};
    readonly minPctOtm = PHA_MIN_PCT_OTM;
    readonly maxPctOtm = PHA_MAX_PCT_OTM;
    readonly sides = PHA_SIDES;

    trackRow(_: number, row: PhaPremiumRow): number {
        return row.id;
    }

    onSideChanged(row: PhaPremiumRow, ev: {value: PhaSide, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event) || !ev.value || ev.value === row.side) {
            return;
        }
        this.sideChanged.emit({row, side: ev.value});
    }

    choiceLabel(c: PhaPremiumChoice): string {
        return c && c.displayName && c.displayName !== c.ticker ? `${c.displayName} (${c.ticker})` : (c ? c.ticker : '');
    }

    /** The selector's value for the row: its choice key (see PhaSheet.premiumChoices). */
    keyOf(row: PhaPremiumRow): string {
        return premiumChoiceKeyOf(row);
    }

    /** The underlying as a TradingInstrument, for `ets-strikes-dropdown` (disabled while null). Cached per ticker. */
    instrumentFor(ticker: string): TradingInstrument | null {
        if (!ticker) {
            return null;
        }
        if (!(ticker in this._instrumentCache)) {
            this._instrumentCache[ticker] = this._instruments.getInstrumentByTicker(ticker) || null;
        }
        return this._instrumentCache[ticker];
    }

    /**
     * The shared strike dropdown's `strikeChanged`. It carries no `event` field, and it also re-emits
     * on its own (undefined) when the expiration changes and its stale strike is not in the new list —
     * while the sheet has already resolved the row's strike. Such an echo is dropped: only a value
     * that differs from the row's is a pick.
     */
    onStrikePicked(row: PhaPremiumRow, ev: {value: number, previousValue: number}): void {
        if (this.isExpired(row) || isVoid(ev)) {
            return;
        }
        const value = ev.value === null || ev.value === undefined ? null : ev.value;
        if (value === row.strike) {
            return;
        }
        if (value === null && row.strike !== null && row.expiration && row.expiration.strikes.indexOf(row.strike) >= 0) {
            return;
        }
        this.strikePicked.emit({row, strike: value});
    }

    onUnderlyingChanged(row: PhaPremiumRow, ev: {value: string, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event) || !ev.value) {
            return;
        }
        this.underlyingPicked.emit({row, key: ev.value});
    }

    onExpirationChanged(row: PhaPremiumRow, ev: {value: PhaExpiration, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event)) {
            return;
        }
        this.expirationPicked.emit({row, expiration: ev.value || null});
    }

    onContractsChanged(row: PhaPremiumRow, ev: {value: number, event?: any}): void {
        if (row.crossedOut || this.isExpired(row) || isVoid(ev) || isVoid(ev.event) || ev.value === null || ev.value === undefined) {
            return;
        }
        this.contractsPicked.emit({row, contracts: ev.value});
    }

    contractsLimit(row: PhaPremiumRow): number | undefined {
        return row.pooled && !this.isExpired(row) ? Math.max(row.contractsAvailable, row.heldContracts,
            row.strategyType === 'Two-for-One' ? row.contractsSelling : 0) : undefined;
    }

    /**
     * A user edit only (`event` set): the box is re-bound to the ACTUAL % of the re-selected strike
     * (10 typed -> 10.2 shown), and that programmatic write must not come back as a pick.
     */
    onPctOtmChanged(row: PhaPremiumRow, ev: {value: number, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event) || ev.value === null || ev.value === undefined || isNaN(ev.value)) {
            return;
        }
        if (ev.value === row.pctOtm) {
            return;
        }
        this.pctOtmPicked.emit({row, pct: ev.value});
    }
}
